import React from "react";
import {
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  Flex,
  Divider,
  Space,
  theme,
  type FormInstance,
} from "antd";
import {
  CheckCircleFilled,
  ShopOutlined,
  BankOutlined,
  TrademarkOutlined,
  MinusCircleOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { SectionHeader, FieldLabel, inputStyle, SmartLoad } from "../../shared";
import type { UserData } from "../../PDFReader/types";
import type { Company } from "../types";
import openNotification from "../../../utils/openNotification";

interface CompanyFormProps {
  form: FormInstance<any>;
  onFinish: (values: Company) => void;
  isPending: boolean;
  buttonText: string;
  formId?: string;
  rfcReadOnly?: boolean;
}

const { Option } = Select;

const CompanyForm: React.FC<CompanyFormProps> = ({
  form,
  onFinish,
  isPending,
  buttonText,
  formId = "company-form",
  rfcReadOnly = false,
}) => {
  const { token } = theme.useToken();
  const iStyle = inputStyle(token);

  const handleDataExtracted = (data: UserData) => {
    form.setFieldsValue({
      rfc_company: data.rfc_company,
      name_company: data.name_company,
    });
    openNotification("success", "Datos extraídos correctamente");
  };

  const formatDates = <T extends { start_date?: any; end_date?: any }>(items: T[]): T[] =>
    items.map((item) => ({
      ...item,
      start_date: item.start_date ? item.start_date.format("YYYY-MM-DD") : null,
      end_date: item.end_date ? item.end_date.format("YYYY-MM-DD") : null,
    }));

  const handleFinish = (values: any) => {
    onFinish({
      ...values,
      date_start_operations: values.date_start_operations
        ? values.date_start_operations.format("YYYY-MM-DD")
        : null,
      regimes: formatDates(values.regimes ?? []),
      obligations: formatDates(values.obligations ?? []),
      company_economic_act: (values.company_economic_act ?? []).map((a: any) => ({
        ...a,
        end_date: a.end_date ? a.end_date.format("YYYY-MM-DD") : null,
        percentage: String(a.percentage || 0)
      })),
    });
  };

  return (
    <>
      <SmartLoad
        onDataExtracted={handleDataExtracted}
        description="Sube la Constancia de Situación Fiscal para auto-completar los datos."
      />
      <Form
        id={formId}
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        requiredMark={false}
        style={{ width: "100%" }}
      >


        {/* ── Datos Fiscales ── */}
        <Flex vertical gap={16} style={{ marginBottom: 28 }}>
          <SectionHeader title="Datos Fiscales" />

          <Form.Item
            name="rfc_company"
            label={<FieldLabel text="RFC" />}
            rules={[{ required: true, message: "Campo requerido" }]}
            style={{ marginBottom: 0 }}
          >
            <Input
              size="large"
              placeholder="ABCD000000XXX"
              style={iStyle}
              disabled={rfcReadOnly}
              onInput={(e) =>
                !rfcReadOnly &&
                (e.currentTarget.value = e.currentTarget.value.toUpperCase())
              }
            />
          </Form.Item>

          <Form.Item
            name="name_company"
            label={<FieldLabel text="Denominación / Razón Social" />}
            rules={[{ required: true, message: "Campo requerido" }]}
            style={{ marginBottom: 0 }}
          >
            <Input
              size="large"
              placeholder="Nombre legal de la empresa"
              prefix={<ShopOutlined style={{ color: token.colorSuccess }} />}
              style={iStyle}
            />
          </Form.Item>

          <Form.Item
            name="capital_regime"
            label={<FieldLabel text="Régimen de Capital" />}
            rules={[{ required: true, message: "Campo requerido" }]}
            style={{ marginBottom: 0 }}
          >
            <Input
              size="large"
              placeholder="S.A. de C.V."
              prefix={<BankOutlined style={{ color: token.colorWarning }} />}
              style={iStyle}
            />
          </Form.Item>

          <Form.Item
            name="public_name_company"
            label={<FieldLabel text="Nombre Comercial" />}
            rules={[{ required: true, message: "Campo requerido" }]}
            style={{ marginBottom: 0 }}
          >
            <Input
              size="large"
              placeholder="Nombre de tu marca"
              prefix={<TrademarkOutlined style={{ color: token.colorPrimary }} />}
              style={iStyle}
            />
          </Form.Item>

          <Flex gap={12} wrap>
            <Form.Item
              name="date_start_operations"
              label={<FieldLabel text="Inicio de Operaciones" />}
              style={{ marginBottom: 0, flex: "1 1 140px" }}
            >
              <DatePicker
                size="large"
                style={{ ...iStyle, width: "100%" }}
                format="DD/MM/YYYY"
                placeholder="dd/mm/aaaa"
              />
            </Form.Item>

            <Form.Item
              name="status_sat"
              label={<FieldLabel text="Estatus SAT" />}
              rules={[{ required: true, message: "Campo requerido" }]}
              style={{ marginBottom: 0, flex: "1 1 140px" }}
            >
              <Select size="large" placeholder="Selecciona estatus" style={iStyle}>
                <Option value="activo">Activo</Option>
                <Option value="reactivado">Reactivado</Option>
                <Option value="suspendido">Suspendido</Option>
              </Select>
            </Form.Item>
          </Flex>
        </Flex>

        <Divider style={{ margin: "0 0 28px" }} />

        {/* ── Actividades Económicas ── */}
        <Flex vertical gap={16} style={{ marginBottom: 28 }}>
          <SectionHeader title="Actividades Económicas" />

          <Form.List name="company_economic_act">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...rest }) => (
                  <Space
                    key={key}
                    align="baseline"
                    style={{ display: "flex", marginBottom: 8 }}
                    wrap
                  >
                    <Form.Item {...rest} name={[name, "id_company_economic_act"]} hidden>
                      <Input />
                    </Form.Item>

                    <Form.Item
                      {...rest}
                      name={[name, "economic_activity"]}
                      label={<FieldLabel text="Actividad" />}
                      rules={[{ required: true, message: "Requerido" }]}
                      style={{ marginBottom: 0, minWidth: 160 }}
                    >
                      <Input placeholder="Actividad económica" style={iStyle} />
                    </Form.Item>

                    <Form.Item
                      {...rest}
                      name={[name, "percentage"]}
                      label={<FieldLabel text="Porcentaje" />}
                      rules={[{ required: true, message: "Requerido" }]}
                      style={{ marginBottom: 0, width: 100 }}
                    >
                      <Input placeholder="%" type="number" style={iStyle} />
                    </Form.Item>

                    <Form.Item
                      {...rest}
                      name={[name, "end_date"]}
                      label={<FieldLabel text="Fecha fin" />}
                      style={{ marginBottom: 0 }}
                    >
                      <DatePicker
                        placeholder="Fecha fin"
                        format="DD/MM/YYYY"
                        style={iStyle}
                      />
                    </Form.Item>

                    <MinusCircleOutlined
                      onClick={() => remove(name)}
                      style={{ color: token.colorError, fontSize: 16, marginTop: 28 }}
                    />
                  </Space>
                ))}

                <Form.Item style={{ marginBottom: 0 }}>
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                    style={{ borderRadius: token.borderRadiusLG }}
                  >
                    Agregar actividad económica
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        </Flex>

        <Divider style={{ margin: "0 0 28px" }} />

        {/* ── Regímenes ── */}
        <Flex vertical gap={16} style={{ marginBottom: 28 }}>
          <SectionHeader title="Regímenes Fiscales" />

          <Form.List name="regimes">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...rest }) => (
                  <Space
                    key={key}
                    align="baseline"
                    style={{ display: "flex", marginBottom: 8 }}
                    wrap
                  >
                    <Form.Item {...rest} name={[name, "id_company_regime"]} hidden>
                      <Input />
                    </Form.Item>

                    <Form.Item
                      {...rest}
                      name={[name, "regime"]}
                      label={<FieldLabel text="Régimen" />}
                      rules={[{ required: true, message: "Requerido" }]}
                      style={{ marginBottom: 0, minWidth: 200 }}
                    >
                      <Input placeholder="Régimen fiscal" style={iStyle} />
                    </Form.Item>

                    <Form.Item
                      {...rest}
                      name={[name, "start_date"]}
                      label={<FieldLabel text="Fecha inicio" />}
                      style={{ marginBottom: 0 }}
                    >
                      <DatePicker
                        placeholder="Fecha inicio"
                        format="DD/MM/YYYY"
                        style={iStyle}
                      />
                    </Form.Item>

                    <Form.Item
                      {...rest}
                      name={[name, "end_date"]}
                      label={<FieldLabel text="Fecha fin" />}
                      style={{ marginBottom: 0 }}
                    >
                      <DatePicker
                        placeholder="Fecha fin"
                        format="DD/MM/YYYY"
                        style={iStyle}
                      />
                    </Form.Item>

                    <MinusCircleOutlined
                      onClick={() => remove(name)}
                      style={{ color: token.colorError, fontSize: 16, marginTop: 28 }}
                    />
                  </Space>
                ))}

                <Form.Item style={{ marginBottom: 0 }}>
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                    style={{ borderRadius: token.borderRadiusLG }}
                  >
                    Agregar régimen
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        </Flex>

        <Divider style={{ margin: "0 0 28px" }} />

        {/* ── Obligaciones ── */}
        <Flex vertical gap={16} style={{ marginBottom: 32 }}>
          <SectionHeader title="Obligaciones Fiscales" />

          <Form.List name="obligations">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...rest }) => (
                  <Space
                    key={key}
                    align="baseline"
                    style={{ display: "flex", marginBottom: 8 }}
                    wrap
                  >
                    <Form.Item {...rest} name={[name, "id_company_obligation"]} hidden>
                      <Input />
                    </Form.Item>

                    <Form.Item
                      {...rest}
                      name={[name, "obligation"]}
                      label={<FieldLabel text="Obligación" />}
                      rules={[{ required: true, message: "Requerido" }]}
                      style={{ marginBottom: 0, minWidth: 160 }}
                    >
                      <Input placeholder="Descripción de la obligación" style={iStyle} />
                    </Form.Item>

                    <Form.Item
                      {...rest}
                      name={[name, "desc_due_date"]}
                      label={<FieldLabel text="Periodicidad" />}
                      rules={[{ required: true, message: "Requerido" }]}
                      style={{ marginBottom: 0, minWidth: 120 }}
                    >
                      <Input placeholder="Ej. Mensual" style={iStyle} />
                    </Form.Item>

                    <Form.Item
                      {...rest}
                      name={[name, "start_date"]}
                      label={<FieldLabel text="Fecha inicio" />}
                      style={{ marginBottom: 0 }}
                    >
                      <DatePicker
                        placeholder="Fecha inicio"
                        format="DD/MM/YYYY"
                        style={iStyle}
                      />
                    </Form.Item>

                    <Form.Item
                      {...rest}
                      name={[name, "end_date"]}
                      label={<FieldLabel text="Fecha fin" />}
                      style={{ marginBottom: 0 }}
                    >
                      <DatePicker
                        placeholder="Fecha fin"
                        format="DD/MM/YYYY"
                        style={iStyle}
                      />
                    </Form.Item>

                    <MinusCircleOutlined
                      onClick={() => remove(name)}
                      style={{ color: token.colorError, fontSize: 16, marginTop: 28 }}
                    />
                  </Space>
                ))}

                <Form.Item style={{ marginBottom: 0 }}>
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                    style={{ borderRadius: token.borderRadiusLG }}
                  >
                    Agregar obligación
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        </Flex>

        {/* ── Submit ── */}
        <Button
          type="primary"
          htmlType="submit"
          size="large"
          loading={isPending}
          icon={<CheckCircleFilled />}
          block
          style={{
            height: 54,
            borderRadius: token.borderRadiusLG,
            fontWeight: 800,
            fontSize: 16,
            letterSpacing: "0.01em",
            boxShadow: `0 8px 20px ${token.colorPrimaryBg}`,
          }}
        >
          {buttonText}
        </Button>
      </Form>
    </>
  );
};

export default CompanyForm;
